import React, { useMemo } from 'react';
import { ModalInput } from 'src/client/components/recruitments/Modal/SearchConditionModal/ModalInput';
import { DailyWageInput } from 'src/client/components/recruitments/Modal/SearchConditionModal/DailyWageInput';
import { useRecruitmentListContext } from 'client/contexts/recruitments/RecruitmentList';
import { Button } from 'client/components/common/Button';
import { useRouter } from 'next/router';
import { useUrlQueryParams } from 'client/hooks/recruitment/RecruitmentList/useUrlQueryParams';
import {
  License,
  WorkingHourOption,
  AreaTreeResponseRegion,
  JobBenefit,
  JobTask,
  FacilityFeature,
} from 'src/client/generated';
import { WorkingHoursInput } from './WorkingHoursInput';
import { OnlyRecruitingCheckbox } from './OnlyRecruitingCheckbox';

type Props = {
  jobBenefits: JobBenefit[];
  licenses: License[];
  facilityFeatures: FacilityFeature[];
  workingHourOptions: WorkingHourOption[];
  regions: AreaTreeResponseRegion[];
  jobTasks: JobTask[];
};

export const ConditionsList = ({
  licenses,
  facilityFeatures,
  regions,
  workingHourOptions,
  jobBenefits,
  jobTasks,
}: Props) => {
  const router = useRouter();
  const {
    searchQueryParams: {
      date,
      areaIds,
      licenseIds,
      facilityFeatureIds,
      jobBenefitIds,
      sortOptionId,
      workingHourOptionIds,
      maximumWorkingHours,
      maximumDailyWage,
      minimumDailyWage,
      jobTaskIds,
      onlyRecruiting,
    },
    dispatch,
  } = useRecruitmentListContext();

  const { urlQueryParams } = useUrlQueryParams({
    date,
    areaIds,
    licenseIds,
    facilityFeatureIds,
    jobBenefitIds,
    sortOptionId,
    maximumDailyWage,
    minimumDailyWage,
    workingHourOptionIds,
    maximumWorkingHours,
    jobTaskIds,
    onlyRecruiting,
  });

  const licenseIdsSelectedValue = useMemo(() => {
    return licenses.filter((o) => licenseIds?.includes(o.id)).map((o) => o.name);
  }, [licenseIds, licenses]);

  const facilityFeaturesSelectedValue = useMemo(() => {
    return facilityFeatures.filter((o) => facilityFeatureIds?.includes(o.id)).map((o) => o.name);
  }, [facilityFeatureIds, facilityFeatures]);

  const areaIdsSelectedValue = useMemo(() => {
    const selectedArray: string[] = [];
    regions.map((o) =>
      o.areaLList.map((x) => {
        if (areaIds?.includes(x.id)) {
          selectedArray.push(x.name);
        }
      }),
    );
    return selectedArray;
  }, [areaIds, regions]);

  const jobBenefitsSelectedValue = useMemo(() => {
    return jobBenefits.filter((j) => jobBenefitIds?.includes(j.id)).map((j) => j.name);
  }, [jobBenefitIds, jobBenefits]);

  const jobTasksSelectedValue = useMemo(() => {
    return jobTasks.filter((j) => jobTaskIds?.includes(j.id)).map((j) => j.name);
  }, [jobTaskIds, jobTasks]);

  const workingHourOptionIdSelectedValue = useMemo(() => {
    return workingHourOptions.filter((w) => workingHourOptionIds?.includes(w.id)).map((w) => w.name);
  }, [workingHourOptionIds, workingHourOptions]);

  const handleClickAreaSelect = () => {
    dispatch({
      type: 'changeDisplayMode',
      payload: { displayMode: 'area-modal' },
    });
  };

  const handleClickLicenseSelect = () => {
    dispatch({
      type: 'changeDisplayMode',
      payload: { displayMode: 'license-modal' },
    });
  };

  const handleClickFacilityFeature = () => {
    dispatch({
      type: 'changeDisplayMode',
      payload: { displayMode: 'facility-feature-modal' },
    });
  };

  const handleClickWorkingHourOptionSelect = () => {
    dispatch({
      type: 'changeDisplayMode',
      payload: { displayMode: 'working-hour-option-modal' },
    });
  };

  const handleClickJobBenefitSelect = () => {
    dispatch({
      type: 'changeDisplayMode',
      payload: { displayMode: 'job-benefit-modal' },
    });
  };

  const handleClickJobTaskSelect = () => {
    dispatch({
      type: 'changeDisplayMode',
      payload: { displayMode: 'job-task-modal' },
    });
  };

  const handleClickSearch = () => {
    router.push(
      {
        query: urlQueryParams,
      },
      {
        // @ts-ignore
        pathname: router.query.name,
        query: urlQueryParams,
      },
      { shallow: true },
    );
    handleClickClose();
  };

  const handleClickClose = () => {
    dispatch({
      type: 'changeDisplayMode',
      payload: { displayMode: 'list' },
    });
  };

  const handleChangeMaximumWorkingHours = (hours: number | null) => {
    dispatch({
      type: 'changeMaximumWorkingHours',
      payload: { maximumWorkingHours: hours },
    });
  };

  return (
    <>
      <div className="mx-4">
        <ModalInput
          title="エリア"
          value={areaIdsSelectedValue}
          onClick={handleClickAreaSelect}
          arrowIcon={true}
          dataTestId={'search-condition-area'}
        />
        <ModalInput
          title="応募に必要な資格"
          value={licenseIdsSelectedValue}
          onClick={handleClickLicenseSelect}
          arrowIcon={true}
          dataTestId={'search-condition-license'}
        />

        <ModalInput
          title="サービス種別"
          value={facilityFeaturesSelectedValue}
          onClick={handleClickFacilityFeature}
          arrowIcon={true}
          dataTestId={'search-condition-facility-feature'}
        />
        <ModalInput
          title="時間帯"
          value={workingHourOptionIdSelectedValue}
          onClick={handleClickWorkingHourOptionSelect}
          arrowIcon={true}
          dataTestId={'search-condition-working-hour-option'}
        />
        <DailyWageInput
          title="日給"
          maxDailyWage={maximumDailyWage ?? undefined}
          minDailyWage={minimumDailyWage ?? undefined}
        />
        <WorkingHoursInput title="労働時間" onChange={handleChangeMaximumWorkingHours} value={maximumWorkingHours} />
        <ModalInput
          title="求人の特徴"
          value={jobBenefitsSelectedValue}
          onClick={handleClickJobBenefitSelect}
          arrowIcon={true}
          dataTestId={'search-condition-job-benefit'}
        />
        <ModalInput
          title="仕事内容"
          value={jobTasksSelectedValue}
          onClick={handleClickJobTaskSelect}
          arrowIcon={true}
          dataTestId={'search-condition-job-task'}
        />
        <OnlyRecruitingCheckbox />
        {!areaIds?.length && (
          <div className="mt-2">
            <span className="text-xs text-red font-bold">エリアを選択してください</span>
          </div>
        )}
      </div>

      <div className="modal-submit">
        <div className="fixed bottom-0 w-full text-center">
          <div className="w-full border-b border-solid border-gray"></div>
          <Button text="検索する" onClick={handleClickSearch} active={!!areaIds?.length} disabled={!areaIds?.length} />
        </div>
      </div>
    </>
  );
};
